import { HERO_LAYOUTS, HERO_SCHEMA } from "./heroLayouts.constants";
import { OUR_ADVANTAGE_LAYOUTS, OUR_ADVANTAGE_SCHEMA } from "./ourAdvantageLayouts.constants";
import { FAQ_LAYOUTS, FAQ_SCHEMA, FAQ_CK_DEFAULT_PROPS } from "./faqLayouts.constants";
import { THANK_YOU_LAYOUTS, THANK_YOU_SCHEMA } from "./thankYouLayouts.constants";
import { NAVBAR_LAYOUTS, NAVBAR_SCHEMA } from "./navbarLayouts.constants";
import { FOOTER_LAYOUTS, FOOTER_SCHEMA } from "./footerLayouts.constants";
import { FEATURES_GRID_LAYOUTS, FEATURES_GRID_SCHEMA } from "./featuresGridLayouts.constants";
import { QUIZ_LAYOUTS, QUIZ_SCHEMA, QUIZ_CK_DEFAULT_PROPS } from "./quizLayouts.constants";
import { TERMS_PAGE_LAYOUTS, TERMS_PAGE_SCHEMA, TERMS_PAGE_CK_DEFAULT_PROPS } from "./termsPageLayouts.constants";
import { PRIVACY_PAGE_LAYOUTS, PRIVACY_PAGE_SCHEMA, PRIVACY_PAGE_CK_DEFAULT_PROPS } from "./privacyPageLayouts.constants";
import { DASHBOARD_THEME_LAYOUTS, DASHBOARD_THEME_SCHEMA, DASHBOARD_THEME_DEFAULT_PROPS, DASHBOARD_THEME_ALL_BRAND_DEFAULTS } from "./dashboardThemeLayouts.constants";
import { DASHBOARD_PAGES_THEME_LAYOUTS, DASHBOARD_PAGES_THEME_SCHEMA, DASHBOARD_PAGES_THEME_DEFAULT_PROPS, DASHBOARD_PAGES_THEME_ALL_BRAND_DEFAULTS } from "./dashboardPagesThemeLayouts.constants";
import { resolveWrapDefault } from "./wrapControl.constants";
import {
    StructuralNavbarPreview,
    StructuralHeroPreview,
    StructuralFeaturesGridPreview,
    StructuralOurAdvantagePreview,
    StructuralFAQPreview,
    StructuralThankYouPreview,
    StructuralFooterPreview,
    StructuralQuizPreview,
    StructuralTermsPreview,
    StructuralPrivacyPreview,
    StructuralDashboardThemePreview,
    StructuralDashboardPagesThemePreview,
} from "./structuralPreviews";

const brandCode = process.env.REACT_APP_PROJECT_CODE || "CK";

// Homepage sections an admin can freely reorder relative to each other —
// every other homepage element (testimonials, delivery banners, etc.) is
// hardcoded, non-Builder content on every client and stays anchored to
// whichever of these keys it currently renders next to (see each client's
// own page.tsx/chef-kitchen.tsx SECTION_ANCHORS_AFTER map) — reordering only
// ever rearranges THIS list, never those anchors.
//
// "menuSelection" (added per real client feedback, 2026-09-16 — the client
// was testing Layout Order and specifically asked to be able to move Quiz
// relative to the diet/meal-plan picker) represents the same component on
// every brand: CK/E4F's `<DietSelection />`, MEELZ's `<PerfectPlan />`. It's
// `reorderOnly: true` below — it has no Builder-editable content or
// alternate "inactive" version of its own (it's always driven live by the
// backend pack/pricing APIs), so it exists in this list purely as a
// position marker: reorderable, but with no Active/Shown toggle of its own
// and never independently selectable in the main Components picker either.
//
// "featuresGrid" is deliberately EXCLUDED here (even on MEELZ, the only
// brand where it renders as its own section at all) — it's a fixed
// companion to Hero, not an independent section: CK/E4F edit it as fields
// nested inside Hero's own schema (no separate render at all), while MEELZ
// renders it as a separate component but always fixed immediately after
// Hero (see meelz-client-v2's page.tsx SECTION_ANCHORS_AFTER.hero). Letting
// it be reordered independently would only ever be meaningful for one of
// the three brands and would desync it from Hero on that one.
const REORDERABLE_SECTION_KEYS = [
    "hero",
    "quiz",
    "menuSelection",
    "ourAdvantage",
    "faq",
];

const SECTION_LABEL_BY_KEY = {
    hero: "Hero Section",
    quiz: "Wellness Quiz",
    menuSelection: "Menu Selection",
    ourAdvantage: "Our Advantage",
    faq: "FAQ / Q&A",
};

// ── Component registry (brand-agnostic, replaces REGISTRY_MEELZ/E4F/CK/DEMO) ──
// Layouts belong to a component type, not a brand — see plan.md "Critical
// architectural correction". Every active entry (navbar/hero/ourAdvantage/
// faq/thankYou/footer) uses the same `layouts` map shape. featuredMeals
// stays commented out, still out of scope for this pass (see plan.md scope,
// "Deferred / explicitly out of scope for this pass") and keeps its old
// flat (non-layout) shape since it was never migrated.
const COMPONENT_REGISTRY = {
    navbar: {
        key: "navbar",
        label: "Navigation Bar",
        layouts: NAVBAR_LAYOUTS,
        schema: NAVBAR_SCHEMA,
        Preview: StructuralNavbarPreview,
    },
    hero: {
        key: "hero",
        label: "Hero Section",
        layouts: HERO_LAYOUTS,
        schema: HERO_SCHEMA,
        Preview: StructuralHeroPreview,
    },
    // MEELZ-only: matches the real, separate <Features/> component
    // (meelz-client-v2's src/components/features.tsx) rendered directly
    // below the hero section — E4F/CK have no equivalent standalone
    // section (their identical-content features grid stays nested inside
    // hero's own schema for those 2 brands, unchanged). Gated per-brand
    // here rather than always registering it, since there is nothing
    // meaningful to configure for E4F/CK — the entry wouldn't correspond
    // to anything live on their sites at all.
    ...(brandCode === "MEELZ" ? {
        featuresGrid: {
            key: "featuresGrid",
            label: "Features Grid",
            layouts: FEATURES_GRID_LAYOUTS,
            schema: FEATURES_GRID_SCHEMA,
            Preview: StructuralFeaturesGridPreview,
        },
    } : {}),
    ourAdvantage: {
        key: "ourAdvantage",
        label: "Our Advantage",
        layouts: OUR_ADVANTAGE_LAYOUTS,
        schema: OUR_ADVANTAGE_SCHEMA,
        Preview: StructuralOurAdvantagePreview,
    },
    faq: {
        key: "faq",
        label: "FAQ / Q&A",
        layouts: FAQ_LAYOUTS,
        schema: FAQ_SCHEMA,
        Preview: StructuralFAQPreview,
    },
    quiz: {
        key: "quiz",
        label: "Wellness Quiz",
        layouts: QUIZ_LAYOUTS,
        schema: QUIZ_SCHEMA,
        Preview: StructuralQuizPreview,
    },
    // Pure position marker — see REORDERABLE_SECTION_KEYS's own comment
    // above for why this has no `layouts`/`schema`/`Preview` of its own and
    // is never independently selectable in the main Components picker
    // (excluded the same way "featuresGrid" is, in ClientBuilderPanel.js).
    // `reorderOnly: true` tells the Layout Order panel's per-row renderer
    // to skip the Active/Shown switches for this row (there's no Builder
    // content or alternate version of this section to toggle between).
    menuSelection: {
        key: "menuSelection",
        label: "Menu Selection",
        reorderOnly: true,
    },
    thankYou: {
        key: "thankYou",
        label: "Thank-You Page",
        layouts: THANK_YOU_LAYOUTS,
        schema: THANK_YOU_SCHEMA,
        Preview: StructuralThankYouPreview,
    },
    // Flat entry (no `layouts`) — just one field, `order`, an array of the
    // REORDERABLE_SECTION_KEYS above in the sequence they should render.
    // No Preview (a reorderable list has nothing meaningful to visually
    // preview) — ClientBuilderPanel already only renders the Preview column
    // when `entry.Preview` is truthy, so this is a no-op, not a gap.
    sectionOrder: {
        key: "sectionOrder",
        label: "Layout Order",
        defaultProps: { order: REORDERABLE_SECTION_KEYS },
        schema: [
            {
                key: "order",
                label: "Homepage section order",
                type: "sectionOrderList",
                options: REORDERABLE_SECTION_KEYS.map((key) => ({ value: key, label: SECTION_LABEL_BY_KEY[key] })),
            },
        ],
    },
    // featuredMeals: {
    //     key: "featuredMeals",
    //     label: "Featured Meals",
    //     defaultProps: featuredMealsDefaults,
    //     schema: [
    //         { key: "sectionTitle", label: "Section title",    type: "text"    },
    //         { key: "accentColor",  label: "Accent color",     type: "color"   },
    //         { key: "cardBg",       label: "Card background",  type: "color"   },
    //         { key: "showBadge",    label: "Show 'New' badge", type: "boolean" },
    //     ],
    //     Preview: FeaturedMealsPreview,
    // },
    footer: {
        key: "footer",
        label: "Footer",
        layouts: FOOTER_LAYOUTS,
        schema: FOOTER_SCHEMA,
        Preview: StructuralFooterPreview,
    },
    // Standalone routes (/terms, /privacy), not homepage sections — single
    // layout each (no visual-style variants), full rich-text control over
    // page content instead. Unlike thankYou (also standalone, single-layout)
    // these are NOT excluded from the `visible` toggle — see the
    // `selectedKey !== "thankYou"` guard below the schema loop; `visible`
    // here is confirmed to gate the same Structural/Default fallback as
    // `active` does, not a route-removal/404 mechanism.
    termsPage: {
        key: "termsPage",
        label: "Terms of Service Page",
        layouts: TERMS_PAGE_LAYOUTS,
        schema: TERMS_PAGE_SCHEMA,
        Preview: StructuralTermsPreview,
    },
    privacyPage: {
        key: "privacyPage",
        label: "Privacy Policy Page",
        layouts: PRIVACY_PAGE_LAYOUTS,
        schema: PRIVACY_PAGE_SCHEMA,
        Preview: StructuralPrivacyPreview,
    },
    // Client Dashboard tab — chrome/theme only (see plan-dashboard-theme.md).
    // Single layout, colors-only schema, no content fields — replaces the
    // dashboard's hardcoded sidebar/active-nav/promo-card/mobile-accent
    // colors. Deliberately NOT rendered under the "Client site" tab's
    // component picker (see BuilderComponent.js's own "Client dashboard" tab
    // and its dedicated single-entry ClientBuilderPanel instance) — kept in
    // this same shared registry purely for its generic buildDefaultEntry/
    // mergeEntry/publish/history machinery, exactly as plan-dashboard-theme.md
    // confirmed is safe for any new key.
    dashboardTheme: {
        key: "dashboardTheme",
        label: "Dashboard Theme",
        layouts: DASHBOARD_THEME_LAYOUTS,
        schema: DASHBOARD_THEME_SCHEMA,
        Preview: StructuralDashboardThemePreview,
    },
    // Per-page dashboard CONTENT colors (plan-dashboard-page-theme.md) —
    // extends dashboardTheme's shared chrome with per-section colors for the
    // 5 dashboard pages' own content areas. Same "kept in this shared
    // registry purely for its generic machinery, only pickable from the
    // dedicated 'Client dashboard' tab" treatment as dashboardTheme itself.
    dashboardPagesTheme: {
        key: "dashboardPagesTheme",
        label: "Dashboard Pages",
        layouts: DASHBOARD_PAGES_THEME_LAYOUTS,
        schema: DASHBOARD_PAGES_THEME_SCHEMA,
        Preview: StructuralDashboardPagesThemePreview,
    },
};

// Generic helper (strategy-map pattern) — used by any registry entry with a
// `layouts` map, not hardcoded to "hero".
function getLayoutOptions(entry) {
    return Object.entries(entry.layouts).map(([id, def]) => ({ value: id, label: def.label }));
}

// True when a (possibly translatable) field value has no real content in
// any language yet — a plain "" or a {en: "", ru: "", ...} object, but not
// a value where at least one language has been filled in.
function isBlankFieldValue(value) {
    if (value == null) return true;
    if (typeof value === "string") return value === "";
    return Object.values(value).every((v) => !v);
}

// Structural equality for the never-edited backfill checks below — plain
// `===` only catches primitives (colors, strings); array/object fields (e.g.
// faq's `items`) are freshly deserialized from the saved JSON response, so
// they're never the same reference as entry.layouts[id].defaultProps even
// when their content is byte-for-byte identical. A naive JSON.stringify
// comparison is NOT safe here — confirmed via a real saved row where the
// backend/CardsEditor round-trip reordered each item's object keys (e.g.
// {icon, answer, category, question} vs the source file's {icon, category,
// question, answer}) with byte-identical values, which JSON.stringify
// reports as different strings purely due to key order, causing the
// backfill to wrongly treat unedited saved data as a genuine admin edit.
// This recurses structurally instead, ignoring key order at every level.
function isSameValue(a, b) {
    if (a === b) return true;
    if (typeof a !== typeof b) return false;
    if (a == null || b == null) return false;
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
        return a.every((item, i) => isSameValue(item, b[i]));
    }
    if (typeof a === "object") {
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;
        return aKeys.every((key) => isSameValue(a[key], b[key]));
    }
    return false;
}

const DEFAULT_LAYOUT_BY_BRAND = { MEELZ: "meelz", E4F: "e4f", CK: "ck" };

// Per-component brand→layout-id lookup for the color-translation feature
// specifically — NOT the same thing as DEFAULT_LAYOUT_BY_BRAND above (that
// one picks which layout the admin STARTS editing on; this one picks which
// layout id's colors are the "real" source of truth to translate FROM, for
// the ACTIVE SESSION'S brand — see getLayoutOwnColors). hero/navbar/footer's
// own layout ids already literally match brand codes (meelz/e4f/ck) AND
// those ids' colors are each brand's real, directly-extracted colors
// (confirmed via each file's own "real values" comments) — DEFAULT_LAYOUT_BY_BRAND
// is a safe, correct source for these three.
// ourAdvantage/faq/thankYou are NOT listed here — their layout_1/2/3
// "brand-inspired" comments (e.g. ourAdvantage's layout_2 "CK-inspired")
// turned out to describe STRUCTURAL STYLE ONLY (card-less big numbers,
// colored-header-band vs minimal, etc.), not real brand colors — e.g.
// ourAdvantage layout_2's actual colors (#E8F0F9/#22C55E) are arbitrary
// placeholders, confirmed by direct comparison against CK's real hero
// colors (#46298A/#9D91B9), which don't match at all. Using them as a
// brand-color source would have been just as wrong as the very first,
// already-rejected invented-color-table approach. All three components
// always fall through to hero's own real per-brand colors instead (see
// getHeroFallbackColorsForBrand, called per-layout-id via getLayoutOwnColors)
// — every brand still gets its own real colors on every layout, just sourced
// from hero uniformly, never from these components' own layout data.
// dashboardTheme/dashboardPagesTheme are deliberately NOT listed here —
// unlike hero/navbar/footer they have no real multi-brand layout ids to
// pick between (only ever "default"), so DEFAULT_LAYOUT_BY_BRAND-style
// per-id lookup doesn't apply. They still need brand-color translation
// though — see DASHBOARD_DEFAULT_PROPS_BY_KEY below and its dedicated
// branch inside getLayoutOwnColors, which sources their colors from the
// current session's REACT_APP_PROJECT_CODE instead of a per-id map. Without
// that branch, mergeEntry's never-edited backfill never runs for these two
// keys, and whichever brand's admin publishes first permanently freezes its
// colors into the single shared DB row every other brand reads back.
const COLOR_SOURCE_LAYOUT_BY_BRAND = {
    hero:   DEFAULT_LAYOUT_BY_BRAND,
    navbar: DEFAULT_LAYOUT_BY_BRAND,
    footer: DEFAULT_LAYOUT_BY_BRAND,
};

// Non-color IDENTITY fields that must never leak across a cross-brand
// layout pick, same principle as COLOR_SOURCE_LAYOUT_BY_BRAND but for
// fields the schema doesn't mark type:"color". A brand's real social media
// accounts are business identity, not a visual style choice — an E4F/CK
// admin picking MEELZ's navbar layout (Layout 1) for its structural look
// should never silently publish MEELZ's real Facebook/Instagram/Telegram
// URLs on their own live site. Confirmed live bug: exactly that happened,
// because socialLinks (type:"cards") was never covered by
// getLayoutOwnColors' color-only backfill — only bgColor/textColor/etc
// (type:"color" fields) ever got translated to the active session's brand.
// Reuses the same COLOR_SOURCE_LAYOUT_BY_BRAND brand->layout-id map (only
// valid for components already in that map) since "this layout id's own
// real content" and "this session's brand's real content" are the same
// lookup either way.
const IDENTITY_FIELD_KEYS_BY_COMPONENT = {
    navbar: ["socialLinks"],
};

// Components with no brand-specific layout of their own — always sourced
// from hero's own real per-brand primaryColor/backgroundColor instead (see
// getHeroFallbackColorsForBrand). Only valid for components whose schema has
// fields literally named `primaryColor`/`backgroundColor` — never guessed
// onto a differently-named field. thankYou's schema uses these exact same
// two field names (thankYouLayouts.constants.js), so it plugs into this
// exact mechanism with no new code — previously excluded entirely (no real
// per-brand layout of its OWN to source from), now included via the same
// hero-fallback path ourAdvantage/faq already use, per explicit request to
// extend brand color translation to every component.
//
// "quiz" was missed in that same pass despite qualifying identically —
// quizLayouts.constants.js's QUIZ_SCHEMA has the same literal
// `backgroundColor`/`primaryColor` fields (only one real layout id,
// "default", so no per-brand layout of its own either). Without it here,
// getLayoutOwnColors returns null for quiz, mergeEntry's never-edited
// backfill never runs, and resetSelectedToDefault/buildDefaultEntry leave
// quiz's colors at whatever raw hardcoded value QUIZ_LAYOUTS.default
// (MEELZ's green) — or, once a brand publishes, that brand's own colors —
// permanently frozen into the shared DB row, regardless of the active
// session's REACT_APP_PROJECT_CODE. Confirmed bug: an E4F admin session
// showed MEELZ's/CK's colors here instead of E4F's own.
const HERO_FALLBACK_COMPONENTS = new Set(["ourAdvantage", "faq", "thankYou", "quiz"]);

// dashboardTheme/dashboardPagesTheme: single "default" layout id, no
// per-brand layout ids at all — unlike hero/navbar/footer, there is no
// "this layout id's own colors" to look up, because every brand shares the
// exact same id. Their real per-brand colors are the current admin
// session's brand (computed once from REACT_APP_PROJECT_CODE, in their own
// constants files) — this is the SAME session-brand lookup getBrandOwnColors
// used to do wrongly for hero/navbar/footer (many real per-brand layout ids
// sharing one row), but here it's actually correct, because there's only
// one id ever, so "the id's own colors" and "this session's brand's colors"
// are the same thing by construction. Without this, mergeEntry's
// never-edited backfill (getLayoutOwnColors returning null) never runs for
// these two, so whichever brand happens to publish first permanently
// freezes its colors into the single shared DB row for every other brand.
const DASHBOARD_DEFAULT_PROPS_BY_KEY = {
    dashboardTheme: DASHBOARD_THEME_DEFAULT_PROPS,
    dashboardPagesTheme: DASHBOARD_PAGES_THEME_DEFAULT_PROPS,
};

// ALL brands' raw defaults (not just the active session's) for the two
// dashboard-only keys — keyed by brand code, e.g. { E4F: {...}, CK: {...},
// MEELZ: {...} }. Needed because the "never edited" check below must
// recognize a saved color as untouched even when it matches a DIFFERENT
// brand's raw default, not only the currently-running session's own brand —
// see isDashboardColorNeverEdited's comment for the full scenario this fixes.
const DASHBOARD_ALL_BRAND_DEFAULTS_BY_KEY = {
    dashboardTheme: DASHBOARD_THEME_ALL_BRAND_DEFAULTS,
    dashboardPagesTheme: DASHBOARD_PAGES_THEME_ALL_BRAND_DEFAULTS,
};

// Confirmed bug: under the current shared testing DB (one DB row read/
// written by every brand's admin, not the isolated-per-brand-deployment
// this file's other color-translation logic assumes — see
// COLOR_SOURCE_LAYOUT_BY_BRAND's header comment), a dashboardTheme/
// dashboardPagesTheme color field that a DIFFERENT brand's admin left
// untouched (still equal to THAT brand's own raw default, e.g. CK's
// "#46298A") gets saved into the shared row. When E4F's admin session then
// loads that same row, the plain "does savedValue match MY raw default"
// check sees CK's purple next to E4F's red raw default, concludes "this
// looks like a genuine deliberate edit" and leaves it alone — permanently
// freezing another brand's color in place. Clicking Reset only rewrites the
// LOCAL, unpublished draft (see resetSelectedToDefault's own comment); the
// next time this row is loaded from the backend, this same mismatch
// recurs, making it look like "Reset doesn't stick." Checking against every
// brand's raw default (not just the active one) instead correctly
// classifies "some brand's real, untouched default" as never-edited in
// every case, so the backfill to the CURRENT brand's real color always
// fires unless an admin has genuinely picked a custom, non-default color.
function isDashboardColorNeverEdited(entryKey, colorKey, savedValue, activeRawDefault) {
    if (savedValue === undefined || savedValue === activeRawDefault) return true;
    const allBrandDefaults = DASHBOARD_ALL_BRAND_DEFAULTS_BY_KEY[entryKey];
    if (!allBrandDefaults) return false;
    return Object.values(allBrandDefaults).some((brandProps) => brandProps[colorKey] === savedValue);
}

// Per-component, per-brand FULL content override (not just colors) — for
// components whose "brand's real version" isn't just a color scheme but
// actual different content (e.g. faq's real questions/answers). Applied
// uniformly across every layout id for that brand's session, same principle
// as getLayoutOwnColors but covering every field the brand's defaultProps
// object declares, not just type:"color" schema fields. "faq" -> "CK"
// (FAQ_CK_DEFAULT_PROPS, CK's real, verbatim-extracted 18 questions —
// previously only reachable via a separate, easily-mis-selected "FAQ (CK)"
// 4th layout option, now removed from FAQ_LAYOUTS) and "quiz" -> "CK"
// (QUIZ_CK_DEFAULT_PROPS, CK's real 5-question wellness quiz, extracted
// verbatim from ck-client-ck's lib/dictionary.ts quizKeys block) are
// populated today. MEELZ/E4F FAQ content stays the existing generic
// layout_1/2/3 placeholders; quiz has only ever had MEELZ's seed content —
// add entries here only once a brand's real content is available to
// extract, same bar navbar/hero/footer's real per-brand data already met.
const FULL_CONTENT_OVERRIDE_BY_KEY_AND_BRAND = {
    faq: { CK: FAQ_CK_DEFAULT_PROPS },
    quiz: { CK: QUIZ_CK_DEFAULT_PROPS },
    termsPage: { CK: TERMS_PAGE_CK_DEFAULT_PROPS },
    privacyPage: { CK: PRIVACY_PAGE_CK_DEFAULT_PROPS },
};

// Fields EXCLUDED from the full-content override above, per component key —
// these define a layout id's own structural identity (which visual variant
// it renders as), not brand-owned content/color. FAQ_CK_DEFAULT_PROPS was
// originally authored as a single, standalone "FAQ (CK)" layout entry with
// its own fixed `displayStyle: "tabbed"` (matching CK's real live site) —
// now that the same object is applied across layout_1/2/3 uniformly, that
// fixed value would otherwise stomp layout_2's "accordion" / layout_3's
// "grid" the moment Object.assign ran, making the layout picker change
// nothing but the "Display style" field's own label while the actual
// preview kept rendering as tabbed regardless of which layout was selected
// (real bug, found via live testing). `displayStyle` must stay sourced from
// each layout id's OWN entry.layouts[id].defaultProps, never the brand
// override, for exactly this reason.
const FULL_CONTENT_OVERRIDE_EXCLUDED_FIELDS_BY_KEY = {
    faq: new Set(["displayStyle"]),
};

function getFullContentOverride(entry) {
    const override = FULL_CONTENT_OVERRIDE_BY_KEY_AND_BRAND[entry.key]?.[brandCode];
    if (!override) return null;
    const excluded = FULL_CONTENT_OVERRIDE_EXCLUDED_FIELDS_BY_KEY[entry.key];
    if (!excluded) return override;
    return Object.fromEntries(
        Object.entries(override).filter(([field]) => !excluded.has(field))
    );
}

// hero's own real per-brand primaryColor/backgroundColor — the two color
// roles every brand definitely has a real, directly-extracted value for
// (confirmed via heroLayouts.constants.js's own "real values" comments).
// Takes an explicit brand CODE (e.g. "MEELZ") — NOT the module-level
// `brandCode` (current admin session) — see getLayoutOwnColors's own header
// comment for why this distinction is load-bearing.
function getHeroFallbackColorsForBrand(forBrandCode) {
    const brandLayoutId = DEFAULT_LAYOUT_BY_BRAND[forBrandCode];
    const heroLayout = brandLayoutId && HERO_LAYOUTS[brandLayoutId];
    if (!heroLayout) return null;
    return {
        primaryColor: heroLayout.defaultProps.primaryColor,
        backgroundColor: heroLayout.defaultProps.backgroundColor,
    };
}

// Returns the color values that should apply to a given layout `id`, for
// THIS session's active brand (module-level `brandCode`, from
// REACT_APP_PROJECT_CODE) — e.g. for entry === NAVBAR and any `id`
// ("meelz"/"e4f"/"ck"), a CK admin session always gets CK's real navbar
// colors, regardless of which structural layout is selected. Every brand
// runs its own fully isolated deployment/DB (brand baked in at Docker build
// time — see gwt-admin's CLAUDE.md), so there is no cross-brand row to
// corrupt: this session's brandCode is the only brand that will ever read or
// write this DB's rows, making "translate every layout id to the active
// brand" safe. (Earlier revisions of this function instead resolved colors
// from the LAYOUT ID being processed, so switching structural layouts also
// changed color scheme — e.g. CK's admin picking "Layout 1" would show
// MEELZ's colors. Per explicit request, every layout option's colors now
// follow the active brand instead, so changing structural layout never
// changes color scheme.)
function getLayoutOwnColors(entry, id) {
    const colorKeys = (entry.schema || [])
        .filter((f) => f.type === "color")
        .map((f) => f.key);
    if (colorKeys.length === 0) return null;

    const dashboardDefaults = DASHBOARD_DEFAULT_PROPS_BY_KEY[entry.key];
    if (dashboardDefaults) {
        return Object.fromEntries(colorKeys.map((key) => [key, dashboardDefaults[key]]));
    }

    if (HERO_FALLBACK_COMPONENTS.has(entry.key)) {
        const fallback = getHeroFallbackColorsForBrand(brandCode);
        if (!fallback) return null;
        return Object.fromEntries(colorKeys.map((key) => [key, fallback[key]]));
    }

    const brandLayoutMap = COLOR_SOURCE_LAYOUT_BY_BRAND[entry.key];
    if (!brandLayoutMap) return null;
    const activeBrandLayoutId = brandLayoutMap[brandCode];
    const activeBrandLayout = activeBrandLayoutId && entry.layouts[activeBrandLayoutId];
    if (!activeBrandLayout) return null;
    return Object.fromEntries(colorKeys.map((key) => [key, activeBrandLayout.defaultProps[key]]));
}

// Same lookup as getLayoutOwnColors, for IDENTITY_FIELD_KEYS_BY_COMPONENT's
// non-color fields instead of type:"color" schema fields — see that
// constant's own comment for why (navbar's socialLinks specifically).
function getLayoutOwnIdentityFields(entry, id) {
    const identityKeys = IDENTITY_FIELD_KEYS_BY_COMPONENT[entry.key];
    if (!identityKeys || identityKeys.length === 0) return null;

    const brandLayoutMap = COLOR_SOURCE_LAYOUT_BY_BRAND[entry.key];
    if (!brandLayoutMap) return null;
    const activeBrandLayoutId = brandLayoutMap[brandCode];
    const activeBrandLayout = activeBrandLayoutId && entry.layouts[activeBrandLayoutId];
    if (!activeBrandLayout) return null;
    return Object.fromEntries(identityKeys.map((key) => [key, activeBrandLayout.defaultProps[key]]));
}

// Generic default-shape builder (strategy-map pattern) — branches on whether
// a registry entry has a `layouts` map, not on component key. Layout-capable
// entries (every active entry today) get the nested { layout, active,
// byLayout } shape; flat entries (featuredMeals, while it stays out of
// scope/commented out) keep their existing { ...defaultProps } shape.
function buildDefaultEntry(entry) {
    if (entry.layouts) {
        const brandMapped = DEFAULT_LAYOUT_BY_BRAND[brandCode];
        const defaultLayout = (brandMapped && entry.layouts[brandMapped])
            ? brandMapped
            : Object.keys(entry.layouts)[0];
        // Translate EVERY layout's color fields (every type:"color" schema
        // field — background, text, button, top bar, all of them) to THIS
        // SESSION'S ACTIVE BRAND's real colors — see getLayoutOwnColors —
        // so every structural layout option looks like the active brand,
        // never a mix of brands depending on which layout id happens to be
        // selected. Safe because every brand runs its own fully isolated
        // deployment/DB (brand baked in at Docker build time), so there is
        // no shared row for another brand's admin to ever read back.
        // Content/alignment/arrangement/text fields are completely
        // untouched; only fields the schema itself marks type:"color" are
        // overridden. This is seed/default data only, exactly like every
        // other defaultProps value: fully admin-editable afterward via the
        // normal color picker at any time, and a real published color
        // (including a real admin edit) always wins over this default via
        // mergeEntry, same as any other field.
        return {
            layout: defaultLayout,
            active: false,
            // Whole-section show/hide — independent of `active` (which only
            // chooses published-layout vs. hardcoded-fallback, never hides
            // anything). Defaults to true so a brand-new entry is visible
            // out of the box, same as every real published component today.
            visible: true,
            byLayout: Object.fromEntries(
                Object.entries(entry.layouts).map(([id, def]) => {
                    const layoutDefaults = { ...def.defaultProps };
                    const fullOverride = getFullContentOverride(entry);
                    if (fullOverride) {
                        // Takes priority over getLayoutOwnColors below — it
                        // already includes that brand's own real colors
                        // alongside its real content, so no need to also
                        // run the color-only override afterward.
                        Object.assign(layoutDefaults, fullOverride);
                    } else {
                        const idColors = getLayoutOwnColors(entry, id);
                        if (idColors) {
                            for (const [field, value] of Object.entries(idColors)) {
                                if (value !== undefined) layoutDefaults[field] = value;
                            }
                        }
                        const idIdentityFields = getLayoutOwnIdentityFields(entry, id);
                        if (idIdentityFields) {
                            for (const [field, value] of Object.entries(idIdentityFields)) {
                                if (value !== undefined) layoutDefaults[field] = value;
                            }
                        }
                    }
                    return [id, layoutDefaults];
                })
            ),
        };
    }
    return { ...(entry.defaultProps || {}) };
}

// Generic entry-shape reader (strategy-map pattern) — the single place that
// knows how to pull "the props currently in effect" out of an entry's data,
// branching on entry.layouts exactly like buildDefaultEntry/mergeEntry do.
// Was previously re-implemented at every call site in ClientBuilderPanel.js
// (selected component's props, featuresGrid's edit-panel props, featuresGrid's
// preview props) — same expression, same fallback, copy-pasted.
function readEntryProps(entry, data) {
    if (!data) return {};
    return entry.layouts ? (data.byLayout?.[data.layout] || {}) : data;
}

// Generic entry-shape writer (strategy-map pattern) — the write-side
// counterpart to readEntryProps. Returns a new entry object with `key` set to
// `val` at the correct depth (byLayout[current.layout][key] for layout-capable
// entries, [key] directly for flat ones), leaving everything else in `current`
// untouched. `current` must already be a fully-shaped entry (as returned by
// buildDefaultEntry/mergeEntry) — same precondition every inline call site
// already relied on before this was extracted.
function writeEntryProp(entry, current, key, val) {
    if (entry.layouts) {
        const layout = current.layout;
        return {
            ...current,
            byLayout: { ...current.byLayout, [layout]: { ...current.byLayout[layout], [key]: val } },
        };
    }
    return { ...current, [key]: val };
}

// ── Panel ─────────────────────────────────────────────────────

// mergeEntry's field-normalizer pipeline (strategy-map pattern) — each rule
// below is a self-contained normalizer with the signature
// (merged, ctx) => merged, mutating and returning the same `merged` object
// (matching the original inline loops exactly, so identity/mutation
// semantics are unchanged). `ctx` (built once per layout id inside
// mergeEntry) carries whatever that one rule needs — `savedLayout` (that
// layout's saved props, before defaults were applied), `originalDefaults`
// (defaults.byLayout[id], i.e. this layout's OWN pre-merge, already
// brand-color-translated default props), and `rawLayoutDefaults`
// (entry.layouts[id].defaultProps, the untranslated data-file original —
// only applyColorBrandTranslation needs this distinction; every other rule
// uses originalDefaults). None of these rules currently depend on another
// rule's output, so the listed order isn't load-bearing today, but keeping
// select/source/wrap/text ahead of the color backfill mirrors the original
// inline code's order.

// Rule: a saved value that isn't one of the field's known select options is
// replaced with that layout's default. Derived from entry.schema directly so
// this covers hero, ourAdvantage, and any future layout-capable component
// automatically — no global constant needed.
function applySelectValidation(merged, { originalDefaults, selectValidValues }) {
    for (const [field, validValues] of Object.entries(selectValidValues)) {
        if (!validValues.has(merged[field])) {
            merged[field] = originalDefaults[field];
        }
    }
    return merged;
}

// Rule: imagePicker-type fields have a companion "<key>Source" field (by
// convention) that must default specifically to "library" when a saved row
// predates Step 10 and has no imageSource at all — a "missing" here always
// means "library", not "invalid → fall back to schema default", so this is
// its own rule, not folded into the select-validation rule above.
function applyImagePickerSourceDefaults(merged, { imagePickerSourceKeys }) {
    for (const sourceKey of imagePickerSourceKeys) {
        if (merged[sourceKey] == null) merged[sourceKey] = "library";
    }
    return merged;
}

// Rule: wrapControl-type fields (plan-v3.md Step 4) have the same implicit
// "<key>Wrap" companion-prop convention. A missing wrap value defaults to
// whatever CSS behavior that field already had, hardcoded, before this
// control existed (see wrapControl.constants.js's WRAP_DEFAULT_OVERRIDES —
// "normal" for every field except navbar's `ctaText`/`navLinks[].label`,
// which already rendered as effectively non-wrapping) — so no existing
// published row's appearance changes the moment this ships. Top-level
// wrapControl fields only — cards sub-fields (e.g. navbar's navLinks[].label)
// live inside each card object, not a top-level key, so they're backfilled
// per-item in CardsEditor's own read path instead.
function applyWrapDefaults(merged, { entryKey, wrapKeys }) {
    for (const wrapKey of wrapKeys) {
        const companionKey = `${wrapKey}Wrap`;
        if (merged[companionKey] == null) {
            merged[companionKey] = resolveWrapDefault(entryKey, wrapKey);
        }
    }
    return merged;
}

// Rule: top-level `type: "text"` fields always carry real, publishable seed
// content in DEFAULT_PROPS (see heroLayouts.constants.js / footerLayouts.
// constants.js — brand-name text was rewritten to be brand-agnostic wording,
// never left blank). A blank saved value — including a real stored "" from a
// past bug where these fields were briefly seeded empty — is never a
// legitimate authored state for these fields, unlike genuinely optional
// fields (none of these schemas mark a text field optional today), so it
// always falls back to the current default instead of publishing/previewing
// empty. A saved value with any real content, in any language, is left
// completely untouched.
function applyTextBlankFallback(merged, { originalDefaults, textKeys }) {
    for (const textKey of textKeys) {
        if (isBlankFieldValue(merged[textKey])) {
            merged[textKey] = originalDefaults[textKey];
        }
    }
    return merged;
}

// Rule: color fields, for the brand-translation backfill described in
// buildDefaultEntry/getBrandOwnColors — a row published BEFORE this feature
// shipped still has each layout's OLD, un-translated color baked into the
// database, which (correctly, by the usual merge rule) would otherwise beat
// the new brand-translated default forever. If a saved color is byte-for-byte
// identical to that layout's OWN original, never-brand-translated color
// (i.e. the admin never actually picked a color for this field), it's safe
// to backfill it to the brand's real color — any value that differs at all
// is a genuine admin edit and is left untouched, exactly like the text-field
// backfill above.
//
// Deliberately compares against `rawLayoutDefaults` (entry.layouts[id].
// defaultProps, the ORIGINAL data-file value) — NOT `originalDefaults`
// (defaults.byLayout[id]), which buildDefaultEntry has already brand-color-
// translated. Comparing against the already-translated value would make
// "never edited" always true for a fresh brand-translated default, which is
// harmless the first time but would also swallow a genuine admin edit that
// happens to match a DIFFERENT brand's real color by coincidence — the raw,
// pre-translation value is the only correct "did the admin ever touch this"
// baseline.
function applyColorBrandTranslation(merged, { savedLayout, rawLayoutDefaults, colorKeys, brandColors }) {
    if (!brandColors) return merged;
    for (const colorKey of colorKeys) {
        const savedValue = savedLayout[colorKey];
        const neverEdited = savedValue === undefined || savedValue === rawLayoutDefaults[colorKey];
        if (neverEdited && brandColors[colorKey] !== undefined) {
            merged[colorKey] = brandColors[colorKey];
        }
    }
    return merged;
}

// Rules run in this fixed order for every layout id being merged.
const MERGE_FIELD_NORMALIZERS = [
    applySelectValidation,
    applyImagePickerSourceDefaults,
    applyWrapDefaults,
    applyTextBlankFallback,
    applyColorBrandTranslation,
];

// Generic merge (strategy-map pattern) — branches on entry.layouts, not on
// component key. Layout-capable entries merge byLayout per-id (so a partially
// saved config doesn't lose other layouts' defaults); flat entries merge as
// before. Each layout's saved+default props are combined via a plain spread,
// then run through MERGE_FIELD_NORMALIZERS in order (see each rule's own
// comment above for what it backfills and why).
function mergeEntry(entry, defaults, saved) {
    if (entry.layouts) {
        if (!saved) return defaults;
        const selectValidValues = Object.fromEntries(
            (entry.schema || [])
                .filter((f) => f.type === "select")
                .map((f) => [f.key, new Set(f.options.map((o) => o.value))])
        );
        const imagePickerSourceKeys = (entry.schema || [])
            .filter((f) => f.type === "imagePicker")
            .map((f) => `${f.key}Source`);
        const textKeys = (entry.schema || [])
            .filter((f) => f.type === "text")
            .map((f) => f.key);
        const wrapKeys = (entry.schema || [])
            .filter((f) => f.wrapControl)
            .map((f) => f.key);
        const colorKeys = (entry.schema || [])
            .filter((f) => f.type === "color")
            .map((f) => f.key);
        // paymentMethodTexts-type fields (currently only thankYou's
        // `paymentMethodTexts`) hold a fixed-shape object one level deeper
        // than every other field this function backfills — the generic
        // shallow `{ ...defaults.byLayout[id], ...savedLayout }` spread
        // below only replaces the whole object wholesale, so a saved object
        // missing a key (e.g. a row saved before "Tabby" existed, or from an
        // interrupted partial save) would otherwise lose that method
        // forever. Backfilled one level deeper, below, after the shallow
        // merge — same "saved wins, missing keys fall back to schema
        // default" rule as every loop above, just one level deeper.
        const paymentMethodTextsKeys = (entry.schema || [])
            .filter((f) => f.type === "paymentMethodTexts")
            .map((f) => f.key);
        // The `layout` pointer itself must resolve to a REAL key of the
        // current `defaults.byLayout` — a saved value pointing at a no
        // -longer-existing layout id (e.g. thankYou's old "layout_1"/
        // "layout_2"/"layout_3" after this ships, now collapsed to just
        // "default") would otherwise make every downstream `byLayout[layout]`
        // lookup (this file's own `props` resolution, and the live client's
        // equivalent lookup) silently resolve to `undefined`/`{}`, even
        // though the migration lookup above correctly populated
        // `byLayout.default` with the real carried-forward values. Falls
        // back to `defaults.layout` (the brand's real default layout id)
        // exactly like `active`'s own `??` fallback below — never left
        // pointing at a dead key.
        const resolvedLayout = Object.prototype.hasOwnProperty.call(defaults.byLayout, saved.layout)
            ? saved.layout
            : defaults.layout;
        return {
            layout: resolvedLayout,
            active: saved.active ?? defaults.active,
            visible: saved.visible ?? defaults.visible,
            byLayout: Object.fromEntries(
                Object.keys(defaults.byLayout).map((id) => {
                    // In-application migration lookup (plan-v4.md Step 4):
                    // thankYou's old shape had 3 layout keys (`layout_1`/
                    // `layout_2`/`layout_3`); THANK_YOU_LAYOUTS now has only
                    // `default`. A saved row from before this shipped has no
                    // `byLayout.default` yet, but does have
                    // `byLayout[saved.layout]` (whichever of the 3 old keys
                    // was actually active) — read THAT as this id's saved
                    // data instead of an empty object, so old primaryColor/
                    // backgroundColor/customNote values surface correctly on
                    // first read with no separate script or database write.
                    // Harmless no-op for every other component (their
                    // `saved.layout` is already a real key of their own
                    // `byLayout`, so `saved.byLayout?.[id]` already matches
                    // and this fallback is never reached).
                    const savedLayout = saved.byLayout?.[id] || saved.byLayout?.[saved.layout] || {};
                    const merged = { ...defaults.byLayout[id], ...savedLayout };
                    for (const [field, validValues] of Object.entries(selectValidValues)) {
                        if (!validValues.has(merged[field])) {
                            merged[field] = defaults.byLayout[id][field];
                        }
                    }
                    for (const sourceKey of imagePickerSourceKeys) {
                        if (merged[sourceKey] == null) merged[sourceKey] = "library";
                    }
                    for (const wrapKey of wrapKeys) {
                        const companionKey = `${wrapKey}Wrap`;
                        if (merged[companionKey] == null) {
                            merged[companionKey] = resolveWrapDefault(entry.key, wrapKey);
                        }
                    }
                    for (const textKey of textKeys) {
                        if (isBlankFieldValue(merged[textKey])) {
                            merged[textKey] = defaults.byLayout[id][textKey];
                        }
                    }
                    for (const pmtKey of paymentMethodTextsKeys) {
                        merged[pmtKey] = { ...defaults.byLayout[id][pmtKey], ...merged[pmtKey] };
                    }
                    // Sourced from the active session's brand (getLayoutOwnColors)
                    // — see that function's header comment. A layout id's saved
                    // color that still matches its own raw, never-brand-translated
                    // default is backfilled to the active brand's real colors,
                    // so a row published before this feature (or before a brand's
                    // colors were set) picks up the correct colors on next load;
                    // a layout id some admin has genuinely customized is left
                    // untouched.
                    // Full-content override (see buildDefaultEntry/
                    // getFullContentOverride) — same never-edited backfill
                    // rule as the color-only case below, but covering every
                    // field the override declares (content included), not
                    // just type:"color" schema fields. Mutually exclusive
                    // with the color-only backfill: a component with a full
                    // override for this brand skips the narrower one.
                    const fullOverride = getFullContentOverride(entry);
                    if (fullOverride) {
                        const originalLayoutDefaults = entry.layouts[id].defaultProps;
                        for (const field of Object.keys(fullOverride)) {
                            const savedValue = savedLayout[field];
                            const neverEdited = savedValue === undefined
                                || isSameValue(savedValue, originalLayoutDefaults[field]);
                            if (neverEdited && fullOverride[field] !== undefined) {
                                merged[field] = fullOverride[field];
                            }
                        }
                    } else {
                        const idColors = getLayoutOwnColors(entry, id);
                        if (idColors) {
                            const originalLayoutColors = entry.layouts[id].defaultProps;
                            for (const colorKey of colorKeys) {
                                const savedValue = savedLayout[colorKey];
                                const neverEdited = isDashboardColorNeverEdited(
                                    entry.key, colorKey, savedValue, originalLayoutColors[colorKey]
                                );
                                if (neverEdited && idColors[colorKey] !== undefined) {
                                    merged[colorKey] = idColors[colorKey];
                                }
                            }
                        }
                        // Same never-edited backfill as colors above, for
                        // IDENTITY_FIELD_KEYS_BY_COMPONENT's non-color fields
                        // (navbar's socialLinks) — see that constant's own
                        // comment. isSameValue (deep-equal), not `===`, since
                        // socialLinks is an array of {platform, url} objects,
                        // not a primitive — same reasoning as the cards-field
                        // backfill this file's own isSameValue was written for.
                        const idIdentityFields = getLayoutOwnIdentityFields(entry, id);
                        if (idIdentityFields) {
                            const originalLayoutDefaults = entry.layouts[id].defaultProps;
                            for (const field of Object.keys(idIdentityFields)) {
                                const savedValue = savedLayout[field];
                                const neverEdited = savedValue === undefined
                                    || isSameValue(savedValue, originalLayoutDefaults[field]);
                                if (neverEdited && idIdentityFields[field] !== undefined) {
                                    merged[field] = idIdentityFields[field];
                                }
                            }
                        }
                    }
                    return [id, merged];
                })
            ),
        };
    }
    const merged = { ...defaults, ...(saved || {}) };
    // sectionOrder's `order` array must always be exactly today's
    // REORDERABLE_SECTION_KEYS set, never whatever a saved row happens to
    // contain — a row saved before a new reorderable section existed would
    // otherwise silently drop that section from rendering entirely (see each
    // client's own sectionOrder consumption: missing keys just never
    // render), and a stale key from a component that's since been removed
    // from REORDERABLE_SECTION_KEYS (e.g. "featuresGrid", removed — see that
    // constant's own comment for why) would be harmlessly ignored
    // client-side but is still worth cleaning up here. Keeps the admin's
    // saved order for every key still valid, drops anything no longer
    // reorderable, and appends any newly-added key at the end (same "new
    // section shows up last, nothing existing moves" rule as a brand-new
    // registry entry).
    if (entry.key === "sectionOrder" && Array.isArray(merged.order)) {
        const known = new Set(REORDERABLE_SECTION_KEYS);
        const kept = merged.order.filter((key) => known.has(key));
        const missing = REORDERABLE_SECTION_KEYS.filter((key) => !kept.includes(key));
        merged.order = [...kept, ...missing];
    }
    return merged;
}

export { COMPONENT_REGISTRY, getLayoutOptions, DEFAULT_LAYOUT_BY_BRAND, buildDefaultEntry, mergeEntry, readEntryProps, writeEntryProp };
